import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from '../button';
import { SimpleInput } from '../input';
import { addOneTenantToStore } from 'store/actions/tenant';
import { MultiSelect } from '../select';
import { useWalletService } from 'hooks/api/sales/wallet';
import { GridColumn, GridRow } from 'app/member/layouts/grid';
import { DetailItem } from '../info-section/item';

const credit_types = [
  { label: 'Email Credits', value: 'email' },
  { label: 'SMS Credits', value: 'sms' },
  { label: 'Push Credits', value: 'push' }
];

export const WalletTopUp = ({ tenant_id }) => {
  const dispatch = useDispatch();
  const { fetchWallets, updateWallet } = useWalletService();

  const tenants = useSelector((state) => state.tenants);
  const tenant = tenants[tenant_id];

  const [credits_to_add, setCreditToAdd] = useState(0);
  const [credit_type, setCreditType] = useState({});
  const [email_balance, setEmailBalance] = useState(0);
  const [email_rate, setEmailRate] = useState(0);
  const [push_balance, setPushBalance] = useState(0);
  const [push_rate, setPushRate] = useState(0);
  const [saving, setSaving] = useState(false);
  const [sms_balance, setSMSBalance] = useState(0);
  const [sms_dnd_rate, setSMSDNDRate] = useState(0);
  const [sms_rate, setSMSRate] = useState(0);
  const [top_up_reason, setTopUpReason] = useState('');

  const REASON_OPTIONS = [
    {
      value: 'free-credit',
      label: 'Free Credit'
    },
    {
      value: 'bank-credit',
      label: 'Bank Credit'
    },
    {
      value: 'refund',
      label: 'Refund'
    }
  ];
  useEffect(() => {
    if (tenant?.email) {
      setEmailRate(tenant.email.rate || 0);
      setEmailBalance(tenant.email.balance || 0);
    }

    if (tenant?.sms) {
      setSMSRate(tenant.sms.rate || 0);
      setSMSDNDRate(tenant.sms.dnd_rate || 0);
      setSMSBalance(tenant.sms.balance || 0);
    }

    if (tenant?.push) {
      setPushRate(tenant.push.rate || 0);
      setPushBalance(tenant.push.balance || 0);
    }
  }, [tenant?.id]);

  const submit = async () => {
    const {
      wallets: [wallet]
    } = await fetchWallets({ query_string: `tenant_id=${tenant_id}` });

    let new_balance = Number(wallet?.email?.balance || 0);
    switch (credit_type.value) {
      case 'email':
        new_balance = (wallet?.email?.balance || 0) + Number(credits_to_add);
        break;
      case 'sms':
        new_balance = (wallet?.sms?.balance || 0) + Number(credits_to_add);
        break;
      case 'push':
        new_balance = (wallet?.push?.balance || 0) + Number(credits_to_add);
        break;
      default:
        break;
    }

    setSaving(true);
    let email_data = { ...wallet?.email, rate: Number(email_rate) };
    if (credit_type.value === 'email') {
      email_data = { ...email_data, balance: new_balance };
    }

    let sms_data = { ...wallet?.sms, rate: Number(sms_rate), dnd_rate: Number(sms_dnd_rate) };
    if (credit_type.value === 'sms') {
      sms_data = { ...sms_data, balance: new_balance };
    }

    let push_data = { ...wallet?.push, rate: Number(push_rate) };
    if (credit_type.value === 'push') {
      push_data = { ...push_data, balance: new_balance };
    }

    const result = await updateWallet({
      data: {
        tenant_id,
        topup_volume: Number(credits_to_add),
        reason: top_up_reason.value,
        email: email_data,
        push: push_data,
        sms: sms_data,
        tx_type: credit_type.value
      }
    });

    if (result) {
      setCreditToAdd(() => 0);
      if (credit_type.value === 'email') setEmailBalance(new_balance);
      if (credit_type.value === 'sms') setSMSBalance(new_balance);
      if (credit_type.value === 'push') setPushBalance(new_balance);
      dispatch(
        addOneTenantToStore({
          ...tenant,
          email:
            credit_type.value === 'email'
              ? { ...tenant.email, balance: new_balance }
              : tenant.email,
          sms: credit_type.value === 'sms' ? { ...tenant.sms, balance: new_balance } : tenant.sms,
          push:
            credit_type.value === 'push' ? { ...tenant.push, balance: new_balance } : tenant.push
        })
      );
    }
    setSaving(false);
  };

  return (
    <>
      <GridRow num_of_columns={1}>
        <GridColumn>
          <p>Tenant Name: {tenant?.name}</p>
          <p>Tenant ID: {tenant?.id}</p>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={3}>
        <GridColumn>
          <DetailItem title="Email balance">
            <SimpleInput disabled type="number" value={email_balance} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="SMS balance">
            <SimpleInput disabled type="number" value={sms_balance} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Push balance">
            <SimpleInput disabled type="number" value={push_balance} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={4}>
        <GridColumn>
          <DetailItem title="Email Rate">
            <SimpleInput type="number" value={email_rate} onInput={setEmailRate} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="SMS Normal Rate">
            <SimpleInput type="number" value={sms_rate} onInput={setSMSRate} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="SMS DND Rate">
            <SimpleInput type="number" value={sms_dnd_rate} onInput={setSMSDNDRate} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Push Rate">
            <SimpleInput type="number" value={push_rate} onInput={setPushRate} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <GridRow num_of_columns={3}>
        <GridColumn>
          <DetailItem title="credit type">
            <MultiSelect options={credit_types} value={credit_type} onChange={setCreditType} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="credits">
            <SimpleInput type="number" value={credits_to_add} onInput={setCreditToAdd} />
          </DetailItem>
        </GridColumn>
        <GridColumn>
          <DetailItem title="Reason">
            <MultiSelect options={REASON_OPTIONS} onChange={setTopUpReason} value={top_up_reason} />
          </DetailItem>
        </GridColumn>
      </GridRow>
      <Button onClick={submit} loading={saving} text="Save" icon_name="check_circle" />
    </>
  );
};
